'use client';

import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { ArrowLeft, ThumbsDown, ThumbsUp } from '@/components/ui/lucide-memo';
import StoreSwapAnimation from '@/components/StoreSwapAnimation';
import { ViewSection } from '@/components/portal/ViewSection';

const FONT_FAMILY = 'ui-sans-serif, -apple-system, system-ui, sans-serif';

const CONTENT_STYLE = { fontFamily: FONT_FAMILY };

const LoadingImage = memo(({ src, alt, className }: { src: string; alt: string; className?: string }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setIsLoaded(false);
    setHasError(false);
  }, [src]);

  const handleLoad = useCallback(() => setIsLoaded(true), []);
  const handleError = useCallback(() => setHasError(true), []);

  return (
    <div className="relative">
      {!isLoaded && !hasError && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="h-5 w-5 rounded-full border-2 border-white/10 border-t-white/50 animate-spin" />
        </div>
      )}
      {hasError ? (
        <div className="min-h-[200px] flex items-center justify-center text-[13px] text-white/30">
          Failed to load
        </div>
      ) : (
        <img
          src={src}
          alt={alt}
          className={clsx(className, !isLoaded && 'opacity-0')}
          loading="lazy"
          onLoad={handleLoad}
          onError={handleError}
        />
      )}
    </div>
  );
});
LoadingImage.displayName = 'LoadingImage';

type EvaluationRating = 'good' | 'neutral' | 'bad';

type AnnotationSnapshot = {
  id: string;
  userId: string | null;
  organisationId: string | null;
  userName: string | null;
  organisationName: string | null;
  modelName: string;
  createdAt: string;
  prompt: string | null;
  sourceAgentPrompt?: string | null;
  beforeImageUrl?: string | null;
  afterImageUrl?: string | null;
  referenceImageUrls?: string[] | null;
};

const RATING_OPTIONS: { value: EvaluationRating; label: string; icon: React.ReactNode }[] = [
  { value: 'good', label: 'Good', icon: <ThumbsUp size={14} /> },
  {
    value: 'neutral',
    label: 'Neutral',
    icon: (
      <span className="flex gap-0.5">
        <ThumbsUp size={13} className="text-white/60" />
        <ThumbsDown size={13} className="text-white/60" />
      </span>
    ),
  },
  { value: 'bad', label: 'Bad', icon: <ThumbsDown size={14} /> },
];

type AnnotationStats = {
  evaluatedByYou: number;
  evaluatedByAnyone: number;
  totalPrompts: number;
};

const formatDateTime = (value?: string | null) => {
  if (!value) return 'Unknown';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' }).format(date);
};

interface DataAnnotationDetailViewProps {
  snapshotId: string | null;
  userFilter?: string;
  orgFilter?: string;
  promptFilter?: string;
  onBack: () => void;
}

export const DataAnnotationDetailView = memo(({
  snapshotId: initialSnapshotId,
  userFilter = '',
  orgFilter = '',
  promptFilter = '',
  onBack,
}: DataAnnotationDetailViewProps) => {
  const [snapshot, setSnapshot] = useState<AnnotationSnapshot | null>(null);
  const [selectedRating, setSelectedRating] = useState<EvaluationRating | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showAnimation, setShowAnimation] = useState(false);
  const [nextSnapshotId, setNextSnapshotId] = useState<string | null>(null);
  const [currentSnapshotId, setCurrentSnapshotId] = useState<string | null>(initialSnapshotId);
  const [stats, setStats] = useState<AnnotationStats>({
    evaluatedByYou: 0,
    evaluatedByAnyone: 0,
    totalPrompts: 0,
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const buildQueryString = useCallback(
    (sid?: string | null) => {
      const params = new URLSearchParams();
      if (sid) params.set('snapshotId', sid);
      if (userFilter) params.set('userId', userFilter);
      if (orgFilter) params.set('orgId', orgFilter);
      if (promptFilter) params.set('prompt', promptFilter);
      return params.toString();
    },
    [orgFilter, promptFilter, userFilter],
  );

  const loadSnapshot = useCallback(async (sid?: string | null) => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const query = buildQueryString(sid);
      const response = await fetch(`/api/annotation/entry?${query}`, { cache: 'no-store' });
      if (!response.ok) throw new Error(`Request failed with status ${response.status}`);
      const data = await response.json();
      setSnapshot(data.snapshot ?? null);
      setStats({
        evaluatedByYou: Number(data.stats?.evaluatedByYou ?? 0),
        evaluatedByAnyone: Number(data.stats?.evaluatedByAnyone ?? 0),
        totalPrompts: Number(data.stats?.totalPrompts ?? 0),
      });
      setNextSnapshotId(data.nextSnapshotId ?? null);
      const resolvedId = data.snapshot?.id ?? null;
      if (resolvedId) setCurrentSnapshotId(resolvedId);
    } catch (error) {
      console.error('[annotation] Failed to load snapshot', error);
      setSnapshot(null);
      setErrorMessage('Unable to load a prompt right now.');
    } finally {
      setSelectedRating(null);
      setIsLoading(false);
    }
  }, [buildQueryString]);

  useEffect(() => {
    void loadSnapshot(currentSnapshotId);
  }, []);

  const promptText = isLoading && !snapshot
    ? 'Loading prompt...'
    : snapshot
      ? snapshot.prompt?.trim() || 'No prompt recorded.'
      : 'No prompts found.';
  const sourceAgentPrompt = snapshot?.sourceAgentPrompt?.trim() || null;
  const beforeImageUrl = snapshot?.beforeImageUrl ?? null;
  const afterImageUrl = snapshot?.afterImageUrl ?? null;
  const referenceImageUrls = snapshot?.referenceImageUrls ?? [];
  const animationStages = useMemo(() => {
    if (!beforeImageUrl || !afterImageUrl) return null;
    return [
      { src: beforeImageUrl, width: 1, label: 'Before' },
      { src: afterImageUrl, width: 1, label: 'After' },
    ];
  }, [afterImageUrl, beforeImageUrl]);
  const userLabel = snapshot?.userName ?? 'Unknown';
  const orgLabel = snapshot?.organisationName ?? 'Unknown';
  const modelLabel = snapshot?.modelName ?? 'Unknown';
  const ranAtLabel = formatDateTime(snapshot?.createdAt);

  const isBusy = isLoading || isSaving;
  const nextDisabled = isBusy || !snapshot || !selectedRating;
  const skipDisabled = isBusy || !snapshot;

  const handleSkip = useCallback(async () => {
    if (skipDisabled) return;
    if (!nextSnapshotId) { setErrorMessage('No more prompts in this list.'); return; }
    await loadSnapshot(nextSnapshotId);
  }, [loadSnapshot, nextSnapshotId, skipDisabled]);

  const handleNext = useCallback(async () => {
    if (!snapshot || !selectedRating || isSaving) return;
    setIsSaving(true);
    setErrorMessage(null);
    try {
      const response = await fetch('/api/annotation/annotate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ snapshotId: snapshot.id, value: selectedRating }),
      });
      if (!response.ok) throw new Error(`Annotation request failed with status ${response.status}`);
      if (!nextSnapshotId) { setErrorMessage('No more prompts in this list.'); return; }
      await loadSnapshot(nextSnapshotId);
    } catch (error) {
      console.error('[annotation] Failed to save annotation', error);
      setErrorMessage('Unable to save this evaluation.');
    } finally {
      setIsSaving(false);
    }
  }, [loadSnapshot, nextSnapshotId, selectedRating, snapshot, isSaving]);

  const handleToggleAnimation = useCallback(() => setShowAnimation((prev) => !prev), []);

  // Le rating passe par data-rating plutôt qu'une closure par option : sans cela chaque
  // rendu recrée trois handlers pour une liste dont les valeurs sont figées.
  const handleSelectRating = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const value = event.currentTarget.dataset.rating as EvaluationRating | undefined;
    if (value) setSelectedRating(value);
  }, []);

  const META_GRID = 'grid grid-cols-[1fr_1fr_1fr_1fr] items-center';

  return (
    <ViewSection
      contentClassName="!px-0 !py-0 !space-y-0"
      hideViewLabel={true}
    >
      <div className="flex h-full w-full flex-col overflow-hidden text-white" style={CONTENT_STYLE}>
        {/* Breadcrumb header */}
        <div className="h-[56px] shrink-0 border-b border-[#2A2A2A] px-4">
          <div className="flex h-full items-center gap-3">
            <button
              type="button"
              onClick={onBack}
              className="inline-flex h-[24px] w-[24px] items-center justify-center text-white transition-colors hover:text-white"
              aria-label="Back to list"
              title="Back to list"
            >
              <ArrowLeft size={16} strokeWidth={2.4} />
            </button>
            <span className="h-5 w-[2px] bg-white/28" />
            <div className="flex min-w-0 flex-col gap-[2px] justify-center">
              <span className="text-[14px] leading-[14px] font-normal text-white/78">
                Admin / Data
              </span>
              <h1 className="text-[14px] font-normal leading-[14px] tracking-normal text-white">
                Snapshot evaluation
              </h1>
            </div>
            <div className="flex items-center gap-3 ml-6 text-[13px] text-white/45">
              <span>{stats.evaluatedByYou} by you</span>
              <span className="h-3 w-px bg-white/15" />
              <span>{stats.evaluatedByAnyone} by anyone</span>
              <span className="h-3 w-px bg-white/15" />
              <span>{stats.totalPrompts} total</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          <div className="flex flex-col gap-0">
            {/* Toolbar */}
            <div className="flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-2">
                {snapshot && (
                  <span className="text-[13px] text-white/35">ID: {snapshot.id}</span>
                )}
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={showAnimation}
                onClick={handleToggleAnimation}
                className="inline-flex items-center gap-2 h-8 rounded-[9px] px-3 text-[13px] text-white/75 transition-colors hover:bg-[#222222]"
              >
                <span>Animated view</span>
                <span
                  className={clsx(
                    'relative inline-flex h-4 w-7 items-center rounded-full transition-colors',
                    showAnimation ? 'bg-emerald-400/60' : 'bg-white/15',
                  )}
                >
                  <span
                    className={clsx(
                      'inline-block h-3 w-3 transform rounded-full bg-white transition-transform',
                      showAnimation ? 'translate-x-3.5' : 'translate-x-0.5',
                    )}
                  />
                </span>
              </button>
            </div>

            {/* Metadata table */}
            <div className="border border-[#2A2A2A] bg-[#181818] mx-0">
              <div
                className={clsx(META_GRID, 'h-10 bg-[#1D1D1D] border-b border-[#2A2A2A] text-[13px] font-normal text-white/55')}
              >
                <div className="flex h-10 items-center border-r border-[#2A2A2A] px-4">Used by</div>
                <div className="flex h-10 items-center border-r border-[#2A2A2A] px-4">Organisation</div>
                <div className="flex h-10 items-center border-r border-[#2A2A2A] px-4">Model</div>
                <div className="flex h-10 items-center px-4">Ran at</div>
              </div>
              <div className={clsx(META_GRID, 'h-10 border-b border-[#2A2A2A]/50 text-[13px] text-white/85')}>
                <div className="flex h-10 items-center border-r border-[#2A2A2A]/50 px-4">{userLabel}</div>
                <div className="flex h-10 items-center border-r border-[#2A2A2A]/50 px-4">{orgLabel}</div>
                <div className="flex h-10 items-center border-r border-[#2A2A2A]/50 px-4">{modelLabel}</div>
                <div className="flex h-10 items-center px-4">{ranAtLabel}</div>
              </div>
            </div>

            {/* Prompt */}
            <div className="px-4 py-4">
              <div className="text-[13px] text-white/45 mb-2">Prompt</div>
              <p className="text-[14px] text-white/90 leading-relaxed whitespace-pre-wrap">{promptText}</p>
            </div>

            {sourceAgentPrompt && (
              <div className="px-4 pb-4">
                <div className="text-[13px] text-white/45 mb-2">Source agent prompt</div>
                <p className="text-[14px] text-white/75 leading-relaxed whitespace-pre-wrap">{sourceAgentPrompt}</p>
              </div>
            )}

            {/* Images */}
            <div className="px-4 pb-4">
              {showAnimation && animationStages ? (
                <div>
                  <div className="text-[13px] text-white/45 mb-2">Animation</div>
                  <div className="rounded-lg border border-[#2A2A2A] bg-[#181818] overflow-hidden">
                    <StoreSwapAnimation
                      lang="en"
                      stages={animationStages}
                      maxHeight="70vh"
                      className="mx-auto"
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-[13px] text-white/45 mb-2">Before</div>
                    <div className="rounded-lg border border-[#2A2A2A] bg-[#181818] overflow-hidden">
                      {beforeImageUrl ? (
                        <LoadingImage
                          src={beforeImageUrl}
                          alt="Before"
                          className="w-full h-auto max-h-[60vh] object-contain"
                        />
                      ) : (
                        <div className="min-h-[200px] flex items-center justify-center text-[13px] text-white/30">
                          No input image
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-[13px] text-white/45 mb-2">After</div>
                    <div className="rounded-lg border border-[#2A2A2A] bg-[#181818] overflow-hidden">
                      {afterImageUrl ? (
                        <LoadingImage
                          src={afterImageUrl}
                          alt="After"
                          className="w-full h-auto max-h-[60vh] object-contain"
                        />
                      ) : (
                        <div className="min-h-[200px] flex items-center justify-center text-[13px] text-white/30">
                          No output image
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Reference images */}
            {referenceImageUrls.length > 0 && (
              <div className="px-4 pb-4">
                <div className="text-[13px] text-white/45 mb-2">Reference images</div>
                <div className="grid grid-cols-3 gap-3">
                  {referenceImageUrls.map((url, index) => (
                    <div
                      key={`${url}-${index}`}
                      className="rounded-lg border border-[#2A2A2A] bg-[#181818] overflow-hidden"
                    >
                      <LoadingImage
                        src={url}
                        alt={`Reference ${index + 1}`}
                        className="w-full h-auto max-h-[40vh] object-contain"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Evaluation */}
            <div className="px-4 pb-4">
              <div className="text-[13px] text-white/45 mb-3">Evaluation</div>
              <div className="flex items-center gap-2">
                {RATING_OPTIONS.map((option) => {
                  const isSelected = selectedRating === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      data-rating={option.value}
                      onClick={handleSelectRating}
                      aria-pressed={isSelected}
                      disabled={isBusy || !snapshot}
                      className={clsx(
                        'inline-flex items-center gap-1.5 h-9 px-4 rounded-[9px] text-[13px] transition-colors',
                        isSelected
                          ? 'bg-white/12 text-white border border-white/20'
                          : 'bg-transparent text-white/65 border border-[#2A2A2A] hover:bg-[#222222] hover:text-white',
                        (isBusy || !snapshot) && 'opacity-40 cursor-not-allowed',
                      )}
                    >
                      {option.icon}
                      <span>{option.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Footer actions */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-[#2A2A2A]">
              <div className="text-[13px] text-white/30">
                {errorMessage || (isLoading ? 'Loading...' : '')}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleSkip}
                  disabled={skipDisabled}
                  className={clsx(
                    'h-9 px-4 rounded-[9px] text-[13px] text-white/65 border border-[#2A2A2A] transition-colors hover:bg-[#222222] hover:text-white',
                    skipDisabled && 'opacity-40 cursor-not-allowed',
                  )}
                >
                  Skip
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={nextDisabled}
                  className={clsx(
                    'h-9 px-4 rounded-[9px] text-[13px] transition-colors',
                    !nextDisabled
                      ? 'bg-white/10 text-white border border-white/20 hover:bg-white/15'
                      : 'text-white/40 border border-[#2A2A2A] opacity-40 cursor-not-allowed',
                  )}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ViewSection>
  );
});

DataAnnotationDetailView.displayName = 'DataAnnotationDetailView';
